import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2 } from 'lucide-react';
import { CategorySuggestion } from '../../../types';

type AdminSuggestionsPanelProps = {
  categorySuggestions: CategorySuggestion[];
  processingId: string | null;
  onResolveCategory: (suggestion: CategorySuggestion, decision: 'approve' | 'reject') => Promise<void>;
};

const AdminSuggestionsPanel: React.FC<AdminSuggestionsPanelProps> = ({
  categorySuggestions,
  processingId,
  onResolveCategory
}) => {
  const { t } = useTranslation();
  if (categorySuggestions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4 text-center">
        <CheckCircle className="text-emerald-500/20" size={80} />
        <p className="text-sm font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.no_suggestions', 'Ingen endringsforslag')}</p>
      </div>
    );
  }

  return (
    <>
      {categorySuggestions.map((suggestion) => (
        <div key={suggestion.id} className="bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-panel p-6 space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">
                {t('admin.suggestion_from', 'Endringsforslag fra {{name}}', { name: suggestion.userName })}
              </p>
              <h3 className="text-[var(--text-primary)] font-black text-base mt-1">{suggestion.newName}</h3>
              {suggestion.oldName !== suggestion.newName && <p className="text-[var(--text-muted)] text-xs line-through">{suggestion.oldName}</p>}
            </div>
            <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-3xs font-black uppercase">{suggestion.newFeature}</span>
          </div>

          <div className="bg-[var(--bg-input)] p-4 rounded-xl border border-[var(--border-primary)] grid grid-cols-2 gap-4">
            <div>
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase">{t('common.before', 'Før')}</p>
              <p className="text-[var(--text-secondary)] text-xs mt-1 italic line-clamp-3">"{suggestion.oldDescription}"</p>
            </div>
            <div>
              <p className="text-3xs font-black text-emerald-500 uppercase">{t('common.after', 'Etter')}</p>
              <p className="text-[var(--text-secondary)] text-xs mt-1 italic line-clamp-3">"{suggestion.newDescription}"</p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => onResolveCategory(suggestion, 'approve')}
              disabled={processingId === suggestion.id}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-xl text-2xs font-black uppercase flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
            >
              {processingId === suggestion.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} {t('common.approve', 'Godkjenn')}
            </button>
            <button
              onClick={() => onResolveCategory(suggestion, 'reject')}
              disabled={processingId === suggestion.id}
              className="flex-1 bg-red-900/20 hover:bg-red-900/40 text-red-400 py-3 rounded-xl text-2xs font-black uppercase flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
            >
              {t('common.reject', 'Avvis')}
            </button>
          </div>
        </div>
      ))}
    </>
  );
};

export default AdminSuggestionsPanel;
